import type { CapacityValues, DateTimeFilter } from '../../types/components/filter_bar_types';
import { Dayjs } from 'dayjs';
import { offsetToTimeString } from '@pv/common/utils';
import type { Nullable } from '../../types/common';

// Default constants
export const DEFAULT_CAPACITY_VALUES: CapacityValues = {
  minGuests: undefined,
  maxGuests: undefined,
  allowSeated: false,
  allowStanding: false,
};

export const DEFAULT_DATETIME_FILTER: DateTimeFilter = {
  date: null,
  startOffset: null,
  endOffset: null,
};

// Helper functions
export const clearCapacityFilter = (): CapacityValues => ({ ...DEFAULT_CAPACITY_VALUES });

export const clearDateTimeFilter = (): DateTimeFilter => ({ ...DEFAULT_DATETIME_FILTER });

export const updateDateTimeFilter = (
  current: DateTimeFilter | null, 
  updates: Partial<DateTimeFilter>
): DateTimeFilter => ({
  ...(current || DEFAULT_DATETIME_FILTER),
  ...updates,
});

export const getCapacityText = ({
  minGuests,
  maxGuests,
  allowSeated,
  allowStanding,
}: CapacityValues) => {
  const guestRangeText = getGuestRangeText(minGuests, maxGuests);
  const eventStyleText = getEventStyleText(allowSeated, allowStanding);
  return `${eventStyleText}, ${guestRangeText}`;
};

export const getGuestRangeText = (minGuests?: number, maxGuests?: number) => {
  if (!minGuests && !maxGuests) {
    return 'Any number';
  }
  if (minGuests === maxGuests) {
    return `${minGuests} guests`;
  }
  if (maxGuests && !minGuests) {
    return `${maxGuests} guests`;
  }
  if (!maxGuests && minGuests) {
    return `${minGuests} guests`;
  }
  return `${minGuests} - ${maxGuests} guests`;
};

export const getEventStyleText = (
  allowSeated: boolean,
  allowStanding: boolean,
) => {
  if (allowSeated && allowStanding) {
    return 'Seated or Standing';
  }

  if (allowSeated) {
    return 'Seated';
  }
  if (allowStanding) {
    return 'Standing';
  }

  return 'Seated or Standing';
};

export const displayDateAndTime = (
  eventDate: Nullable<Dayjs>,
  startTime: Nullable<number>,
  endTime: Nullable<number>,
) => {
  if (!eventDate && !startTime && !endTime) {
    return 'Any Date & Time';
  }

  const displayDate = eventDate?.format('MMM DD, YYYY') ?? 'Any Date';

  if (!startTime || !endTime) {
    return `${displayDate} at Any Time`;
  }

  const displayStartTime = offsetToTimeString(startTime);
  const displayEndTime = offsetToTimeString(endTime);

  return `${displayDate} at ${displayStartTime} - ${displayEndTime}`;
}

